import React from 'react';
import { ArrowRight } from 'lucide-react';
import SEOHead from '../../components/SEOHead';

const AIAnalyticsSolution = () => {
  return (
    <>
      <SEOHead
        title="AI Analytics Solutions | Agility"
        description="Advanced AI analytics and business intelligence solutions for data-driven decisions."
        url="https://www.agilitytech.ai/solutions/ai-analytics"
      />
    <div className="pt-20">
      {/* Hero Section */}
      <section className="relative h-[500px] overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://images.unsplash.com/photo-1639322537504-6427a16b0a28?auto=format&fit=crop&q=80"
            alt="AI Analytics Banner"
            className="w-full h-full object-cover opacity-40"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-black via-black/80 to-black/60" />
        </div>
        <div className="absolute inset-0 flex items-center">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8">
            <div className="max-w-3xl">
              <h1 className="text-5xl md:text-7xl font-bold text-white mb-6 leading-tight">
                AI & Predictive Analytics
              </h1>
              <p className="text-xl text-gray-300 leading-relaxed max-w-2xl">
                Harness the power of artificial intelligence and advanced analytics to detect patterns, 
                prevent fraud, and drive data-informed decision making.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-12 bg-black">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-7xl mx-auto">
            <div className="bg-gray-900/50 backdrop-blur-sm rounded-2xl shadow-xl overflow-hidden border border-gray-800/50">
              <div className="grid md:grid-cols-3 divide-y md:divide-y-0 md:divide-x divide-gray-800/50">
                <div className="px-8 py-12 text-center">
                  <div className="text-5xl font-bold text-teal-400 mb-3">77.8%</div>
                  <p className="text-lg text-gray-300">Live prediction accuracy</p>
                </div>
                <div className="px-8 py-12 text-center">
                  <div className="text-5xl font-bold text-teal-400 mb-3">$3M+</div>
                  <p className="text-lg text-gray-300">Saved in chargebacks</p>
                </div>
                <div className="px-8 py-12 text-center">
                  <div className="text-5xl font-bold text-teal-400 mb-3">80%</div>
                  <p className="text-lg text-gray-300">Reduction in manual reviews</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-20 bg-black">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-4xl font-bold text-white mb-8">
              AI-Powered Fraud Detection for Caribbean Food Delivery Marketplace
            </h2>
            
            <div className="prose prose-lg max-w-none">
              <p className="text-xl text-gray-300 mb-12">
                When a fast-growing Caribbean food delivery platform began losing significant revenue 
                to fraudulent orders, they turned to us to build a machine-learning solution that 
                could detect and block fraud in real time. Leveraging their transaction and order 
                data along with our expertise in AWS SageMaker and delivery-management platforms 
                like Yelo and Tookan we delivered a world-class model that achieved 77.8% accuracy 
                in live operation, saving the client millions in prevented chargebacks.
              </p>

              <div className="bg-gray-900/50 backdrop-blur-sm rounded-2xl p-8 shadow-lg mb-12 border border-gray-800/50">
                <h3 className="text-2xl font-bold text-white mb-6">Key Challenges</h3>
                <div className="grid md:grid-cols-2 gap-8">
                  <div className="flex items-start space-x-4">
                    <div className="flex-shrink-0 mt-1">
                      <ArrowRight className="h-6 w-6 text-teal-400" />
                    </div>
                    <div>
                      <h4 className="font-semibold text-white mb-2">High Fraud Volume</h4>
                      <p className="text-gray-300">
                        Fraudsters exploited rapid onboarding and lack of unified prevention rules, 
                        resulting in unauthorized orders and chargebacks.
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start space-x-4">
                    <div className="flex-shrink-0 mt-1">
                      <ArrowRight className="h-6 w-6 text-teal-400" />
                    </div>
                    <div>
                      <h4 className="font-semibold text-white mb-2">Fragmented Data</h4>
                      <p className="text-gray-300">
                        Financial data lived separately from order metadata, making end-to-end 
                        analysis difficult.
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start space-x-4">
                    <div className="flex-shrink-0 mt-1">
                      <ArrowRight className="h-6 w-6 text-teal-400" />
                    </div>
                    <div>
                      <h4 className="font-semibold text-white mb-2">Real-Time Needs</h4>
                      <p className="text-gray-300">
                        Manual review was too slow, and batch scoring after the fact only reduced 
                        losses retrospectively.
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start space-x-4">
                    <div className="flex-shrink-0 mt-1">
                      <ArrowRight className="h-6 w-6 text-teal-400" />
                    </div>
                    <div>
                      <h4 className="font-semibold text-white mb-2">Security & Scale</h4>
                      <p className="text-gray-300">
                        Solution needed to run compliantly with low latency and scale during peak periods.
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              <h3 className="text-3xl font-bold text-white mb-8">Our Solution Architecture</h3>
              
              <div className="grid md:grid-cols-2 gap-8">
                <div className="bg-gray-900/50 backdrop-blur-sm p-8 rounded-xl shadow-lg border border-gray-800/50">
                  <h4 className="text-xl font-bold text-white mb-4">Data Pipeline</h4>
                  <ul className="space-y-3 text-gray-300">
                    <li className="flex items-center space-x-3">
                      <div className="w-2 h-2 bg-teal-400 rounded-full"></div>
                      <span>Unified S3 data lake pipeline</span>
                    </li>
                    <li className="flex items-center space-x-3">
                      <div className="w-2 h-2 bg-teal-400 rounded-full"></div>
                      <span>AWS Glue feature engineering</span>
                    </li>
                    <li className="flex items-center space-x-3">
                      <div className="w-2 h-2 bg-teal-400 rounded-full"></div>
                      <span>Real-time data processing</span>
                    </li>
                  </ul>
                </div>
                
                <div className="bg-gray-900/50 backdrop-blur-sm p-8 rounded-xl shadow-lg border border-gray-800/50">
                  <h4 className="text-xl font-bold text-white mb-4">ML Development</h4>
                  <ul className="space-y-3 text-gray-300">
                    <li className="flex items-center space-x-3">
                      <div className="w-2 h-2 bg-teal-400 rounded-full"></div>
                      <span>SageMaker model training</span>
                    </li>
                    <li className="flex items-center space-x-3">
                      <div className="w-2 h-2 bg-teal-400 rounded-full"></div>
                      <span>Hyperparameter optimization</span>
                    </li>
                    <li className="flex items-center space-x-3">
                      <div className="w-2 h-2 bg-teal-400 rounded-full"></div>
                      <span>Ensemble approach</span>
                    </li>
                  </ul>
                </div>

                <div className="bg-gray-900/50 backdrop-blur-sm p-8 rounded-xl shadow-lg border border-gray-800/50">
                  <h4 className="text-xl font-bold text-white mb-4">Deployment</h4>
                  <ul className="space-y-3 text-gray-300">
                    <li className="flex items-center space-x-3">
                      <div className="w-2 h-2 bg-teal-400 rounded-full"></div>
                      <span>Low-latency endpoints</span>
                    </li>
                    <li className="flex items-center space-x-3">
                      <div className="w-2 h-2 bg-teal-400 rounded-full"></div>
                      <span>API Gateway integration</span>
                    </li>
                    <li className="flex items-center space-x-3">
                      <div className="w-2 h-2 bg-teal-400 rounded-full"></div>
                      <span>Automated workflows</span>
                    </li>
                  </ul>
                </div>

                <div className="bg-gray-900/50 backdrop-blur-sm p-8 rounded-xl shadow-lg border border-gray-800/50">
                  <h4 className="text-xl font-bold text-white mb-4">Monitoring</h4>
                  <ul className="space-y-3 text-gray-300">
                    <li className="flex items-center space-x-3">
                      <div className="w-2 h-2 bg-teal-400 rounded-full"></div>
                      <span>Real-time metrics</span>
                    </li>
                    <li className="flex items-center space-x-3">
                      <div className="w-2 h-2 bg-teal-400 rounded-full"></div>
                      <span>Automated retraining</span>
                    </li>
                    <li className="flex items-center space-x-3">
                      <div className="w-2 h-2 bg-teal-400 rounded-full"></div>
                      <span>Performance analytics</span>
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
    </>
  );
};

export default AIAnalyticsSolution;